import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { ThreeDots } from 'react-loader-spinner';
import { AI_FUNCTIONS_BASE_URL } from '../config/api';
import { buildResumeReadiness, formatReadinessPath, readinessSectionLabels } from './resumeReadiness';
import {
  FIELD_LIMITS,
  sanitizeLine,
  sanitizeMultiline,
  sanitizeResumeFormData,
} from './formValidation';

const BLOCKED_PATH_PARTS = ['date', 'present', 'showonresume', '__proto__', 'constructor', 'prototype'];

const ROOT_PATHS = new Set([
  'personalInfo',
  'militaryInfo',
  'workHistory',
  'educationHistory',
  'volunteerWork',
  'skills',
  'selectedSkills',
  'certifications',
  'training',
  'references',
  'languages',
  'organizations',
  'interests',
  'awards',
]);

const KNOWN_ROOTS = new Set([
  ...ROOT_PATHS,
  'professionalSummary',
  'showProfessionalSummary',
  'additionalInformation',
]);

const clean = (value) => String(value ?? '').trim();

const titleCase = (value) =>
  clean(value)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^\w/, (char) => char.toUpperCase());

const toSafeText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

const parsePath = (pathLike) => {
  const raw = clean(pathLike).replace(/\[(\d+)\]/g, '.$1');
  if (!raw) return [];
  return raw
    .split('.')
    .map((part) => (/^\d+$/.test(part) ? Number(part) : part))
    .filter((part) => part !== '');
};

const pathToString = (path) =>
  path.map((part) => (typeof part === 'number' ? String(part) : part)).join('.');

const getByPath = (obj, path) => {
  try {
    return path.reduce((current, segment) => (current == null ? undefined : current[segment]), obj);
  } catch {
    return undefined;
  }
};

const setByPath = (obj, path, value) => {
  if (!path.length) return false;
  let current = obj;
  for (let index = 0; index < path.length - 1; index += 1) {
    const segment = path[index];
    if (current == null || !(segment in current)) return false;
    current = current[segment];
  }
  const finalSegment = path[path.length - 1];
  if (current == null || !(finalSegment in current)) return false;
  current[finalSegment] = value;
  return true;
};

const setByPathCreatingObjects = (obj, path, value) => {
  if (!path.length) return false;
  let current = obj;
  for (let index = 0; index < path.length - 1; index += 1) {
    const segment = path[index];
    const nextSegment = path[index + 1];
    if (typeof segment === 'number') {
      if (!Array.isArray(current) || !current[segment]) return false;
      current = current[segment];
      continue;
    }
    if (current[segment] === undefined) {
      current[segment] = typeof nextSegment === 'number' ? [] : {};
    }
    if (current[segment] === null || typeof current[segment] !== 'object') return false;
    current = current[segment];
  }
  const finalSegment = path[path.length - 1];
  if (typeof finalSegment === 'number') return false;
  current[finalSegment] = value;
  return true;
};

const getFieldName = (path) => {
  const last = path?.[path.length - 1];
  return typeof last === 'string' ? last : '';
};

const inlineFieldLabels = {
  firstName: 'Name',
  email: 'Email',
  city: 'City',
  jobTitle: 'Job Title',
  employer: 'Employer',
  startDate: 'Start Date',
  endDate: 'End Date',
  hoursPerWeek: 'Hours Per Week',
  responsibilities: 'Responsibilities',
  keyAccomplishments: 'Accomplishments',
  employerAddress: 'Employer Location',
  supervisor: 'Supervisor',
  canContactSupervisor: 'Supervisor Contact Preference',
  showOnResume: 'Show On Resume',
  institution: 'Institution',
  degree: 'Degree Or Major',
  graduationYear: 'Graduation Year',
  certificationName: 'Certification Name',
  certificationAuthority: 'Issuing Authority',
};

const getInlineFieldLabel = (path) => {
  const field = getFieldName(path);
  return inlineFieldLabels[field] || formatReadinessPath(pathToString(path));
};

const getReadinessFieldConfig = (path) => {
  const field = getFieldName(path);
  const pathText = pathToString(path);
  if (!field || path.length < 2) return null;
  if (field === 'showOnResume') {
    return { type: 'checkbox', label: 'Show on resume' };
  }
  if (field === 'canContactSupervisor') {
    return {
      type: 'select',
      label: 'Supervisor contact preference',
      options: [
        { value: '', label: 'Select' },
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
    };
  }
  if (field === 'responsibilities' || field === 'keyAccomplishments' || pathText.includes('significantProjects')) {
    return { type: 'textarea', label: getInlineFieldLabel(path) };
  }
  if (field === 'hoursPerWeek') {
    return { type: 'numberText', label: 'Hours per week' };
  }
  if (field === 'email') {
    return { type: 'email', label: 'Email' };
  }
  if (field === 'phoneNumber' || field === 'phone') {
    return { type: 'tel', label: 'Phone number' };
  }
  return { type: 'text', label: getInlineFieldLabel(path) };
};

const sanitizeInlineValue = (value, config) => {
  if (config?.type === 'checkbox') return Boolean(value);
  if (config?.type === 'numberText') {
    return sanitizeLine(value, FIELD_LIMITS.hours).replace(/[^\d]/g, '').slice(0, FIELD_LIMITS.hours);
  }
  if (config?.type === 'textarea') return sanitizeMultiline(value, FIELD_LIMITS.longText);
  return sanitizeLine(value, FIELD_LIMITS.mediumText);
};

const updateFormPath = (formData, path, value, config) => {
  const draft = sanitizeResumeFormData(JSON.parse(JSON.stringify(formData || {})));
  if (!setByPathCreatingObjects(draft, path, sanitizeInlineValue(value, config))) return formData;
  return sanitizeResumeFormData(draft);
};

const extractQuoted = (message) => {
  const matches = [...clean(message).matchAll(/["']([^"']+)["']/g)];
  return matches.length ? matches[matches.length - 1][1] : '';
};

const extractProposedValue = (suggestion) => {
  if (suggestion?.proposed !== undefined && suggestion?.proposed !== null) {
    return toSafeText(suggestion.proposed);
  }
  const message = clean(suggestion?.message);
  const correction = message.match(
    /(?:should be corrected to|should be corrected as|capitalized as|capitalised as|corrected version:?)([^.]+)/i,
  );
  if (correction) {
    return correction[1].replace(/^["'\s]+|["'\s.]+$/g, '').trim();
  }
  return extractQuoted(message);
};

const extractKeywords = (suggestion) => {
  if (suggestion?.payload?.kind === 'skill' && suggestion.payload.keyword) {
    return [sanitizeLine(suggestion.payload.keyword, FIELD_LIMITS.listItem)].filter(Boolean);
  }
  if (Array.isArray(suggestion?.keywords)) {
    return suggestion.keywords
      .map((keyword) => sanitizeLine(keyword, FIELD_LIMITS.listItem))
      .filter(Boolean);
  }
  const message = clean(suggestion?.message);
  const quoted = [...message.matchAll(/["']([^"']+)["']/g)].map((match) => match[1]);
  const source = quoted.length ? quoted : message.slice(message.indexOf(':') + 1).split(/\||,/);
  return source
    .map((keyword) => sanitizeLine(keyword, FIELD_LIMITS.listItem))
    .filter((keyword) => keyword && keyword.length > 1 && !/^[{}[\]]+$/.test(keyword));
};

const isPathSafe = (path, formData) => {
  if (!path.length) return false;
  const root = path[0];
  if (typeof root !== 'string' || !KNOWN_ROOTS.has(root)) return false;
  if (path.length === 1 && ROOT_PATHS.has(root)) return false;
  const normalized = pathToString(path).toLowerCase();
  if (BLOCKED_PATH_PARTS.some((part) => normalized.includes(part))) return false;
  return getByPath(formData, path) !== undefined;
};

const normalizeAiSuggestion = (raw, formData, index) => {
  if (!raw || typeof raw !== 'object') return null;
  const section = clean(raw.section || raw.path || 'AI Cleanup');
  const message = sanitizeMultiline(raw.message || raw.description || '', 600).trim();
  const path = parsePath(raw.path || raw.section);
  const sectionLower = section.toLowerCase();
  const messageLower = message.toLowerCase();
  const isSkillSuggestion =
    sectionLower.includes('skills') &&
    (messageLower.includes('keyword') || messageLower.startsWith('add skill'));
  const proposed = extractProposedValue(raw);
  const safePath = isPathSafe(path, formData);
  const safeKeywords = isSkillSuggestion ? extractKeywords(raw) : [];
  const fixable = (safePath && proposed) || safeKeywords.length > 0;

  if (path.length && !safePath && !safeKeywords.length) return null;
  if (!message && !fixable) return null;
  if (messageLower.includes('[object object]')) return null;
  if (/https?:\/\/|traceback|exception|azure|openai|stack|request id/i.test(message)) return null;

  return {
    id: `ai-${index}-${pathToString(path) || section}`,
    section: path[0] || section || 'AI Cleanup',
    sectionLabel: readinessSectionLabels[path[0]] || titleCase(path[0] || section || 'AI Cleanup'),
    message: message || `Add ${safeKeywords.join(', ')} to skills.`,
    path,
    pathLabel: formatReadinessPath(pathToString(path)),
    current: safePath ? getByPath(formData, path) : raw.current,
    proposed: proposed ? sanitizeMultiline(proposed, FIELD_LIMITS.longText).trim() : '',
    keywords: safeKeywords,
    fixable,
  };
};

const applyOneSuggestion = (formData, suggestion) => {
  const draft = sanitizeResumeFormData(JSON.parse(JSON.stringify(formData || {})));
  if (suggestion.keywords?.length) {
    const merge = (list) => {
      const next = Array.isArray(list) ? [...list] : [];
      suggestion.keywords.forEach((keyword) => {
        if (!next.some((item) => clean(item).toLowerCase() === keyword.toLowerCase())) {
          next.push(keyword);
        }
      });
      return next;
    };
    draft.skills = merge(draft.skills);
    draft.selectedSkills = merge(draft.selectedSkills);
    return draft;
  }
  if (!suggestion.path?.length || !suggestion.proposed) return formData;
  const current = getByPath(draft, suggestion.path);
  const value = typeof current === 'string'
    ? sanitizeMultiline(suggestion.proposed, FIELD_LIMITS.longText).trim()
    : suggestion.proposed;
  return setByPath(draft, suggestion.path, value) ? draft : formData;
};

const groupBySection = (items) =>
  items.reduce((groups, item) => {
    const key = item.sectionLabel || item.section || 'General';
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
    return groups;
  }, {});

function ReadinessInlineEditor({ finding, formData, setFormData }) {
  const path = parsePath(finding.path);
  const config = getReadinessFieldConfig(path);
  const editableData = sanitizeResumeFormData(formData || {});
  const currentValue = getByPath(editableData, path);
  const parentValue = getByPath(editableData, path.slice(0, -1));

  if (!config || parentValue === undefined || typeof setFormData !== 'function') {
    return <span className="readiness-action-label">{finding.actionLabel}</span>;
  }

  const updateValue = (value) => {
    setFormData((current) => updateFormPath(current, path, value, config));
  };

  if (config.type === 'checkbox') {
    return (
      <label className="readiness-inline-checkbox">
        <input
          type="checkbox"
          checked={Boolean(currentValue)}
          onChange={(event) => updateValue(event.target.checked)}
        />
        {config.label}
      </label>
    );
  }

  if (config.type === 'select') {
    return (
      <label className="readiness-inline-field">
        <span>{config.label}</span>
        <select value={toSafeText(currentValue)} onChange={(event) => updateValue(event.target.value)}>
          {config.options.map((option) => (
            <option value={option.value} key={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
    );
  }

  if (config.type === 'textarea') {
    return (
      <label className="readiness-inline-field wide">
        <span>{config.label}</span>
        <textarea
          value={toSafeText(currentValue)}
          onChange={(event) => updateValue(event.target.value)}
          rows={3}
        />
      </label>
    );
  }

  return (
    <label className="readiness-inline-field">
      <span>{config.label}</span>
      <input
        type={config.type === 'numberText' ? 'text' : config.type}
        inputMode={config.type === 'numberText' ? 'numeric' : undefined}
        value={toSafeText(currentValue)}
        onChange={(event) => updateValue(event.target.value)}
      />
    </label>
  );
}

export default function Inspector({ formData, setFormData, setCurrentScreen }) {
  const [running, setRunning] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiFindings, setAiFindings] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const readiness = useMemo(() => buildResumeReadiness(formData), [formData]);
  const deterministicGroups = useMemo(
    () => groupBySection(readiness.findings),
    [readiness.findings],
  );
  const aiGroups = useMemo(() => groupBySection(aiFindings), [aiFindings]);
  const fixableAi = aiFindings.filter((finding) => finding.fixable);

  const runAiAssist = async () => {
    setRunning(true);
    setAiError('');
    try {
      const response = await axios.post(
        `${AI_FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`,
        {
          prompt: 'resumeInspector',
          resumeData: formData,
          userID: formData?.user || formData?.userID,
        },
        { headers: { 'Content-Type': 'application/json' } },
      );
      const rawSuggestions = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.suggestions)
          ? response.data.suggestions
          : [];
      const normalized = rawSuggestions
        .map((suggestion, index) => normalizeAiSuggestion(suggestion, formData, index))
        .filter(Boolean);
      setAiFindings(normalized);
      setSelectedIds([]);
      if (!normalized.length) {
        setAiError('AI Cleanup did not return any safe user-facing suggestions.');
      }
    } catch (error) {
      console.error('AI Cleanup failed:', error);
      setAiError('AI Cleanup is unavailable right now. The preflight checklist still works without it.');
      setAiFindings([]);
      setSelectedIds([]);
    } finally {
      setRunning(false);
    }
  };

  const applySuggestions = (items) => {
    const applicable = items.filter((item) => item.fixable);
    if (!applicable.length || typeof setFormData !== 'function') return;
    setFormData((current) =>
      applicable.reduce((draft, suggestion) => applyOneSuggestion(draft, suggestion), current),
    );
    setAiFindings((current) => current.filter((finding) => !applicable.some((item) => item.id === finding.id)));
    setSelectedIds([]);
  };

  const toggleSelected = (id) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const selectedFindings = aiFindings.filter((finding) => selectedIds.includes(finding.id));

  return (
    <div className="readiness-console">
      <header className="readiness-hero">
        <div>
          <span className="readiness-kicker">One-Shot Preflight</span>
          <h1>Ready to Generate?</h1>
          <p>Check whether the profile has enough grounded evidence before One-Shot builds the resume.</p>
        </div>
        <div className="readiness-hero-actions">
          <button
            type="button"
            onClick={() => setCurrentScreen?.('oneShotResume')}
          >
            Open One-Shot Resume
          </button>
          <small>Use this when you want a quick confidence check first.</small>
        </div>
        <div className={`readiness-status ${readiness.status.toLowerCase().replace(/\s+/g, '-')}`}>
          <span>{readiness.status}</span>
          <strong>{readiness.score}</strong>
          <small>score</small>
        </div>
      </header>

      <section className="readiness-metrics" aria-label="Readiness summary">
        <div>
          <strong>{readiness.counts.required}</strong>
          <span>Required</span>
        </div>
        <div>
          <strong>{readiness.counts.recommended}</strong>
          <span>Recommended</span>
        </div>
        <div>
          <strong>{readiness.counts.optional}</strong>
          <span>Optional</span>
        </div>
      </section>

      <section className="readiness-panel">
        <div className="readiness-panel-heading">
          <div>
            <span>Profile evidence</span>
            <h2>What to Fix Before One-Shot</h2>
          </div>
          <p>{readiness.findings.length ? 'Fix required items first. Recommended items improve federal detail.' : 'No readiness gaps found.'}</p>
        </div>

        {readiness.findings.length === 0 ? (
          <div className="readiness-empty">This profile is ready for One-Shot resume generation.</div>
        ) : (
          Object.entries(deterministicGroups).map(([section, findings]) => (
            <div className="readiness-group" key={section}>
              <h3>{section}</h3>
              <div className="readiness-finding-list">
                {findings.map((finding) => (
                  <article className={`readiness-finding ${finding.severity}`} key={finding.id}>
                    <span className="readiness-badge">{finding.severity}</span>
                    <div>
                      <p>{finding.message}</p>
                      {finding.pathLabel && <small>{finding.pathLabel}</small>}
                    </div>
                    <ReadinessInlineEditor
                      finding={finding}
                      formData={formData}
                      setFormData={setFormData}
                    />
                  </article>
                ))}
              </div>
            </div>
          ))
        )}
      </section>

      <section className="readiness-panel ai-assist-panel">
        <div className="readiness-panel-heading">
          <div>
            <span>Optional</span>
            <h2>AI Cleanup</h2>
          </div>
          <button type="button" onClick={runAiAssist} disabled={running} aria-busy={running}>
            {running ? (
              <span className="readiness-loading">
                <ThreeDots height="14" width="36" color="#4c6ef5" ariaLabel="loading" visible />
                Running AI Cleanup
              </span>
            ) : (
              'Run AI Cleanup'
            )}
          </button>
        </div>
        <p className="readiness-note">
          Optional cleanup can suggest safe edits, but One-Shot is still the main resume generation path.
        </p>
        {aiError && <p className="readiness-error">{aiError}</p>}

        {aiFindings.length > 0 && (
          <>
            {Object.entries(aiGroups).map(([section, findings]) => (
              <div className="readiness-group" key={section}>
                <h3>{section}</h3>
                <div className="readiness-finding-list">
                  {findings.map((finding) => (
                    <article className={`readiness-finding ai ${finding.fixable ? 'fixable' : ''}`} key={finding.id}>
                      {finding.fixable && (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(finding.id)}
                          onChange={() => toggleSelected(finding.id)}
                          aria-label={`Select ${finding.message}`}
                        />
                      )}
                      <div>
                        <p>{finding.message}</p>
                        {finding.keywords?.length > 0 && (
                          <div className="readiness-keywords">
                            {finding.keywords.map((keyword) => (
                              <span key={keyword}>{keyword}</span>
                            ))}
                          </div>
                        )}
                        {finding.proposed && (
                          <small>
                            {finding.pathLabel ? `${finding.pathLabel}: ` : ''}
                            {finding.current !== undefined ? `"${toSafeText(finding.current)}" -> ` : ''}
                            "{finding.proposed}"
                          </small>
                        )}
                      </div>
                      {finding.fixable ? (
                        <button type="button" onClick={() => applySuggestions([finding])}>
                          Apply
                        </button>
                      ) : (
                        <span className="readiness-action-label">Review only</span>
                      )}
                    </article>
                  ))}
                </div>
              </div>
            ))}
            {fixableAi.length > 0 && (
              <div className="readiness-bulk-actions">
                <button
                  type="button"
                  onClick={() => applySuggestions(selectedFindings)}
                  disabled={selectedFindings.length === 0}
                >
                  Apply Selected
                </button>
                <button type="button" onClick={() => applySuggestions(fixableAi)}>
                  Apply All Fixable
                </button>
                <button type="button" onClick={() => {
                  setAiFindings([]);
                  setSelectedIds([]);
                }}>
                  Clear AI Suggestions
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
